/**
 * @fileoverview Internal-traffic classifier for product analytics (WEB-1229).
 *
 * Product insights must count real people only. An event is internal when the
 * signed-in account is staff, a seeded test principal, or an agent, or when
 * the browser itself is automated:
 *
 * - `@loci.dev` and `@loci.city` addresses (staff, seeded dev users, and the
 *   `test.agent*@loci.city` agent principals);
 * - any address containing `test.agent`, wherever it is hosted;
 * - the existing E2E set from `isE2EAnalyticsAccount` (WEB-1212), which also
 *   covers the `@loci.test` fixture suffix;
 * - `navigator.webdriver === true`: Playwright, Puppeteer and agent browsers
 *   set it, so an anonymous agent session is still internal;
 * - the E2E PostHog override (`e2eAnalyticsOverride`), which only test
 *   harnesses set.
 *
 * The address is used for membership only and is never sent to PostHog.
 */
import { e2eAnalyticsOverride } from '@/lib/analytics';
import { isE2EAnalyticsAccount } from '@/lib/analytics/auth';

/** Domains whose accounts are staff, seeded, or agent principals. */
export const INTERNAL_ACCOUNT_DOMAINS: readonly string[] = ['loci.dev', 'loci.city'];

/** True for staff, seeded, fixture, and agent addresses. Empty is not internal. */
export function isInternalAnalyticsAccount(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  if (isE2EAnalyticsAccount(normalized)) return true;
  if (normalized.includes('test.agent')) return true;
  const domain = normalized.slice(normalized.lastIndexOf('@') + 1);
  return INTERNAL_ACCOUNT_DOMAINS.includes(domain);
}

/** True when the page runs under browser automation or the E2E harness. */
export function isAutomatedAnalyticsSession(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.navigator?.webdriver === true) return true;
  return e2eAnalyticsOverride() !== null;
}

/** The `is_internal` property value for an event fired by this viewer. */
export function resolveIsInternal(email: string | null | undefined): boolean {
  return isInternalAnalyticsAccount(email) || isAutomatedAnalyticsSession();
}
